import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibSpinner } from '@chewy/kib-interstitials-react'
import {
  AlertIconType,
  ButtonWithLoader,
  PermissionArea,
  Utils,
} from '@pbt/pbt-ui-components'

import Expander from '~/components/common/lists/Expander'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { NotificationAreaNames } from '~/constants/notifications'
import {
  RX_REQUEST_TASK_TYPE,
  VET_DIET_REQUEST_TASK_TYPE,
} from '~/constants/taskConstants'
import { useTaskMarkAsDoneDisabled } from '~/hooks/useTaskMarkAsDoneDisabled'
import { queueEasterEggEvent } from '~/store/actions/easterEgg'
import { markAllNotificationsAsReadForLinkedItem } from '~/store/actions/notifications'
import {
  clearTasksError,
  deleteTask,
  fetchTask,
  partialEditTask,
} from '~/store/actions/tasks'
import { useGetTaskTypeByTaskId, useTaskEvent } from '~/store/hooks/tasks'
import { getCRUDByArea } from '~/store/reducers/auth'
import {
  getFeatureToggle,
  getNotificationArea,
} from '~/store/reducers/constants'
import { getNotificationsForLinkedItemId } from '~/store/reducers/notifications'
import {
  getTask,
  getTasksError,
  getTasksIsDeleting,
  getTasksIsFetching,
  getTasksIsLoading,
  getTasksValidationError,
} from '~/store/reducers/tasks'
import { EasterEggEvents, type Task } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import RxRequestTaskDetails from './rx-request-task/RxRequestTaskDetails'
import TaskComponent, { type TaskHandle } from './Task'
import { getTaskChanges } from './taskUtils'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'

export interface TaskDetailsProps {
  isChirp?: boolean
  itemId: string
  onClose: () => void
}

const TaskDetails = ({
  isChirp = false,
  itemId,
  onClose,
}: TaskDetailsProps) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(getTasksIsLoading)
  const isFetching = useSelector(getTasksIsFetching)
  const isDeleting = useSelector(getTasksIsDeleting)
  const tasksError = useSelector(getTasksError)
  const task = useSelector(getTask(itemId))
  const tasksValidationError = useSelector(getTasksValidationError)
  const NotificationAreas = useSelector(getNotificationArea)
  const tasksNotificationAreaId = Utils.findConstantIdByName(
    NotificationAreaNames.TASKS,
    NotificationAreas,
  )
  const notifications = useSelector(
    getNotificationsForLinkedItemId(tasksNotificationAreaId, task?.id),
  )
  const isTaskListOracleIntegrationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.TASK_LIST_ORACLE_INTEGRATION),
  )
  const taskPermissions = useSelector(getCRUDByArea(PermissionArea.TASK))

  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const isMarkAsDoneDisabled = useTaskMarkAsDoneDisabled(task)

  const getTaskType = useGetTaskTypeByTaskId()
  const taskType = getTaskType(itemId)
  const isRxRequestTask =
    taskType === RX_REQUEST_TASK_TYPE || taskType === VET_DIET_REQUEST_TASK_TYPE

  const setCloseOnDelete = useCloseAfterCreation(onClose, getTasksIsDeleting)
  const initialTaskRef = useRef<Task | null>(null)
  const taskRef = useRef<TaskHandle>(null)

  const checkErrorsAndClose = () => {
    if (!tasksError) {
      initialTaskRef.current = null
      onClose()
    }
  }
  const setCloseOnSave = useCloseAfterCreation(
    checkErrorsAndClose,
    getTasksIsLoading,
  )
  const { t } = useTranslation('Tasks')

  const TaskType = useTaskEvent()
  const TaskStates = TaskType.states
  const DoneState = Utils.findConstantIdByName('Done', TaskStates)
  const OpenState = Utils.findConstantIdByName('Open', TaskStates)

  const [openNewConversationDialog] = useDialog(
    DialogNames.NEW_CONVERSATION_WITH_STEPS,
  )
  const [openAlert, closeAlert] = useDialog(DialogNames.DISMISSIBLE_ALERT)

  useEffect(() => {
    setTimeout(() => {
      if (task && taskRef.current) {
        initialTaskRef.current = taskRef.current.get()
      }
    }, 0)
  }, [task])

  useEffect(() => {
    if (notifications?.length > 0) {
      dispatch(
        markAllNotificationsAsReadForLinkedItem(
          itemId,
          tasksNotificationAreaId,
        ),
      )
    }
  }, [notifications?.length])

  useEffect(() => {
    if (itemId) {
      dispatch(fetchTask(itemId))
    }
  }, [itemId])

  useEffect(() => {
    if (tasksError) {
      openAlert({
        iconType: AlertIconType.WARN,
        message: tasksError,
        onClose: () => {
          dispatch(clearTasksError())
          closeAlert()
        },
      })
    }
  }, [tasksError])

  if (isRxRequestTask) {
    return <RxRequestTaskDetails itemId={itemId} onClose={onClose} />
  }

  const isDone = task?.stateId === DoneState
  const showStateChange = !task?.forClient

  const isForVCR = task?.forChewyVcr

  const shouldUseVCRBehavior = isTaskListOracleIntegrationEnabled && isForVCR

  const handleSave = (stateId?: string) => {
    if (taskRef.current?.validate()) {
      const newTask = taskRef.current.get()
      const changes = getTaskChanges(initialTaskRef.current!, newTask)

      changes.id = newTask.id
      if (stateId) {
        changes.stateId = stateId
      }

      if (tasksError) {
        dispatch(clearTasksError())
      }

      dispatch(partialEditTask(changes))
      setCloseOnSave()
    }
  }

  const onDeleteRequested = () => {
    setCloseOnDelete()
    dispatch(deleteTask(itemId))
  }

  const markAsOpen = () => handleSave(OpenState)

  const markAsDone = () => {
    dispatch(queueEasterEggEvent({ actionType: EasterEggEvents.TASK_DONE }))
    handleSave(DoneState)
  }

  const messagePetParent = () => {
    const currentTask = taskRef.current?.get()
    openNewConversationDialog({
      clientId: currentTask?.clientId || task?.client,
      patientId: currentTask?.patientId || task?.patient,
      appointmentId: currentTask?.appointmentId || task?.appointment,
    })
  }

  const getUnsavedData = () =>
    task &&
    !tasksValidationError &&
    (taskRef.current?.hasUnsavedChanges() ?? false)

  const Button = isChirp ? KibButtonNew : ButtonWithLoader

  return (
    <Expander
      isConfirmToDelete
      additionalButtons={[
        Boolean(task?.forClient) && (
          <Button
            disabled={isLoading}
            icon={isChirp && isLoading ? <KibSpinner /> : undefined}
            key="message-pet-parent"
            loading={isLoading}
            onClick={messagePetParent}
          >
            {t('Tasks:TASK_DETAILS.BUTTON_SEND_MESSAGE')}
          </Button>
        ),
        isDone && showStateChange && (
          <Button
            color={isChirp ? undefined : 'secondary'}
            disabled={isLoading}
            emphasis={isChirp ? 'secondary' : undefined}
            icon={isChirp && isLoading ? <KibSpinner /> : undefined}
            key="mark-as-open"
            loading={isLoading}
            theme={isChirp ? 'action' : undefined}
            onClick={markAsOpen}
          >
            {t('Tasks:LABEL.MARK_AS_TO-DO')}
          </Button>
        ),
        !isDone && showStateChange && (
          <Button
            disabled={isLoading || isMarkAsDoneDisabled}
            icon={isChirp && isLoading ? <KibSpinner /> : undefined}
            key="mark-as-done"
            loading={isLoading}
            onClick={markAsDone}
          >
            {t('Tasks:LABEL.MARK_AS_DONE')}
          </Button>
        ),
      ]}
      expandedItemClass="task"
      getUnsavedData={getUnsavedData}
      isChirp={isChirp}
      isDeleting={isDeleting}
      isFetching={isFetching}
      isSaving={isLoading}
      item={task}
      onDeleteRequested={
        !isCvcRolesEnabled || taskPermissions.update
          ? onDeleteRequested
          : undefined
      }
      onSaveRequested={
        !isCvcRolesEnabled || taskPermissions.update
          ? () => handleSave()
          : undefined
      }
    >
      <TaskComponent
        ref={taskRef}
        shouldUseVCRBehavior={shouldUseVCRBehavior}
        task={task}
      />
    </Expander>
  )
}

export default TaskDetails
